import React from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-navy text-white relative overflow-hidden">
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-primary via-accent to-primary" />

      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 py-14">

          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1 space-y-5">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/10 p-1.5 flex-shrink-0">
                <img src="/logo.png" alt="PAK School Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-poppins font-700 text-base text-white leading-none">PAK School &amp; College</div>
                <div className="text-xs text-gray-400 mt-0.5">Every Student A Star</div>
              </div>
            </Link>

            <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
              Nurturing academic excellence and personal development in a dynamic learning environment. Empowering every student to reach their full potential.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/pakschoolssystem"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PAK School Facebook"
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-primary flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <i className="fab fa-facebook-f text-sm" />
              </a>
              <a
                href="https://www.instagram.com/pakschoolcollege_mardan"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PAK School Instagram"
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <i className="fab fa-instagram text-sm" />
              </a>
              <a
                href="https://wa.me/923430001144"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PAK School WhatsApp"
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-green-600 flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <i className="fab fa-whatsapp text-sm" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-5">
            <h4 className="font-poppins font-700 text-white text-sm uppercase tracking-wider">
              Quick Links
              <div className="w-8 h-0.5 bg-primary mt-2 rounded-full" />
            </h4>
            <ul className="space-y-2.5">
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About Us' },
                { to: '/admission', label: 'Admission' },
                { to: '/academics', label: 'Academics' },
                { to: '/gallery', label: 'Gallery' },
                { to: '/contact', label: 'Contact Us' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white hover:gap-3 transition-all duration-200 group"
                  >
                    <i className="fas fa-chevron-right text-[9px] text-primary group-hover:text-accent transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Campuses */}
          <div className="space-y-5">
            <h4 className="font-poppins font-700 text-white text-sm uppercase tracking-wider">
              Our Campuses
              <div className="w-8 h-0.5 bg-primary mt-2 rounded-full" />
            </h4>
            <div className="space-y-4">
              {[
                { name: 'Par Hoti Campus', address: 'Nehr Chowk, Swabi Road Mardan', phone: '0343-0001144/55/66/77' },
                { name: 'DHA Campus', address: 'Muqam Chowk', phone: '0343-0001133' },
                { name: 'SMT Campus', address: 'Phase-2 Sector N, House No.1', phone: '0343-0001122' },
              ].map((campus) => (
                <div key={campus.name} className="group">
                  <p className="text-white font-semibold text-sm mb-1">{campus.name}</p>
                  <p className="text-gray-400 text-xs leading-relaxed">{campus.address}</p>
                  <a
                    href={`tel:${campus.phone.replace(/\D/g, '')}`}
                    className="text-primary hover:text-white text-xs transition-colors"
                  >
                    {campus.phone}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Downloads & Contact */}
          <div className="space-y-5">
            <h4 className="font-poppins font-700 text-white text-sm uppercase tracking-wider">
              Resources
              <div className="w-8 h-0.5 bg-primary mt-2 rounded-full" />
            </h4>
            <div className="space-y-3">
              <a
                href="/assets/Newslater.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-primary/30 transition-all duration-200 group"
              >
                <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-file-pdf text-accent text-sm" />
                </div>
                <span className="text-sm text-gray-300 group-hover:text-white transition-colors">Newsletter PDF</span>
              </a>
              <Link
                to="/booklist"
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-primary/30 transition-all duration-200 group"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-book text-primary text-sm" />
                </div>
                <span className="text-sm text-gray-300 group-hover:text-white transition-colors">Book Lists</span>
              </Link>
            </div>

            <div className="pt-2">
              <p className="text-xs text-gray-500 mb-2">Email us at</p>
              <a
                href="mailto:info@pakschoolandcollege.com"
                className="text-sm text-primary hover:text-white transition-colors"
              >
                info@pakschoolandcollege.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500 text-center sm:text-left">
            &copy; {year} PAK School &amp; College System. All rights reserved.
          </p>
          <p className="text-xs text-gray-500">
            Website by{' '}
            <a
              href="https://technologyinnovision.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-white transition-colors"
            >
              Technology Innovision
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
